/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { userSessionClientsRevoke } from "../funcs/userSessionClientsRevoke.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RevokeUserSessionClientRequest,
  RevokeUserSessionClientSecurity,
} from "../models/operations/revokeusersessionclient.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RevokeUserSessionClientMutationVariables = {
  request: RevokeUserSessionClientRequest;
  security?: RevokeUserSessionClientSecurity | undefined;
  options?: RequestOptions;
};

export type RevokeUserSessionClientMutationData = void;

export type RevokeUserSessionClientMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * revokeUserSessionClient userSessionClients
 *
 * @remarks
 * Soft-delete a user_session_client and cascade to the user_sessions it issued. A DCR client stays revoked. A CIMD client does not: its identity is the metadata document URL, so the next /authorize re-resolves that document and registers a fresh row. Durably blocking a CIMD client is admission control's job, not revocation's.
 */
export function useRevokeUserSessionClientMutation(
  options?: MutationHookOptions<
    RevokeUserSessionClientMutationData,
    RevokeUserSessionClientMutationError,
    RevokeUserSessionClientMutationVariables
  >,
): UseMutationResult<
  RevokeUserSessionClientMutationData,
  RevokeUserSessionClientMutationError,
  RevokeUserSessionClientMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRevokeUserSessionClientMutation(client, options),
    ...options,
  });
}

export function mutationKeyRevokeUserSessionClient(): MutationKey {
  return ["@gram/client", "userSessionClients", "revoke"];
}

export function buildRevokeUserSessionClientMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RevokeUserSessionClientMutationVariables,
  ) => Promise<RevokeUserSessionClientMutationData>;
} {
  return {
    mutationKey: mutationKeyRevokeUserSessionClient(),
    mutationFn: function revokeUserSessionClientMutationFn({
      request,
      security,
      options,
    }): Promise<RevokeUserSessionClientMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(userSessionClientsRevoke(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
